import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const BranchContext = createContext(null);
const KEY = "active_branch_id";

export function BranchProvider({ children }) {
  const [branches, setBranches] = useState([]);
  const [activeId, setActiveIdState] = useState(() => localStorage.getItem(KEY) || "");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(
    () => api.get("/branches").then(({ data }) => setBranches(data)),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    api
      .get("/branches")
      .then(({ data }) => {
        if (cancelled) return;
        setBranches(data);
        // Only auto-pick default branch if none is currently selected (read fresh from storage to
        // avoid capturing a stale `activeId` from initial render).
        const currentActive = localStorage.getItem(KEY) || "";
        if (!currentActive && data.length) {
          const def = data.find((b) => b.is_default) || data[0];
          setActiveIdState(def.id);
          localStorage.setItem(KEY, def.id);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setActive = useCallback((id) => {
    setActiveIdState(id);
    localStorage.setItem(KEY, id);
  }, []);

  const active = useMemo(
    () => branches.find((b) => b.id === activeId) || null,
    [branches, activeId],
  );

  const value = useMemo(
    () => ({ branches, active, activeId, setActive, refresh, loading }),
    [branches, active, activeId, setActive, refresh, loading],
  );

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
}

export function useBranch() {
  return useContext(BranchContext);
}
